import { css } from '@emotion/react';
import { Formik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { usePageTitle, usePersistedSearch, usePersistedSort } from '../../Lib';

import { ApiOrgProperty } from '@kaiizen/kai-pro-api';
import { useAuth, useKaiProApiClient, useOrgMarkets, useOrgProperties, usePropertySearch } from '@kaiizen/kai-pro-api-react-state';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import searchIcon from '../../Assets/search-v2.svg';
import { usePropertyIdLabel } from '../../Lib';
import { logError } from '../../Lib/logger';
import { getLatitudeLongitudeFromPlaceResult, useToast } from '../../State';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { commaize } from '@art-suite/art-core-ts';
import { PageLayout } from '../../Layout';
import { NoRecordsPlaceholder, PropertyEditPanel, PropertyListImage, RecordListHeading } from '../../Partials';
import { presentBathCount } from '../../Presenters';
import { LAYOUT2, STRING_CONSTANTS } from '../../Styles';
import {
  Button,
  DataTable,
  FlyOut,
  FormFieldsRequirementNote,
  FormikLabeledInput,
  FormikLabeledSelect,
  GooglePlacesAutocompleteInput,
  StyledForm,
} from '../../Widgets';
import { GenericLoadingPage } from '../GenericPages';

const PROPERTIES_LOAD_BATCH_SIZE = 100;

const rowStyle = css`
  display: flex;
  gap: ${LAYOUT2.spacing1}px;
  align-items: center;
`;

const barStyle = css`
  color: var(--text2);
`;

const SEARCH_BAR_STYLE = css`
  label: SEARCH_BAR_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
  padding: 0 ${LAYOUT2.spacing1}px;
  background: #ffffff;
  border: 1px solid #cfcfcf;
  height: 42px;
  width: 420px;
`;

const SEARCH_ICON_STYLE = css`
  label: SEARCH_ICON_STYLE;
  width: 18px;
  height: 18px;
  opacity: 0.45;
  filter: brightness(0) saturate(100%);
`;

const SEARCH_COLLAPSED_BUTTON_STYLE = css`
  label: SEARCH_COLLAPSED_BUTTON_STYLE;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  background: #ffffff;
  border: 1px solid #cfcfcf;
  cursor: pointer;
  padding: 0;
`;

const SEARCH_ICON_BUTTON_STYLE = css`
  label: SEARCH_ICON_BUTTON_STYLE;
  background: transparent;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const SEARCH_INPUT_STYLE = css`
  label: SEARCH_INPUT_STYLE;
  flex: 1;
  background: transparent;
  border: none;
  color: #1f1f1f;
  outline: none;

  &::placeholder {
    color: #767676;
  }

  &::-webkit-search-cancel-button {
    -webkit-appearance: none;
    appearance: none;
    display: none;
  }

  &::-ms-clear {
    display: none;
    width: 0;
    height: 0;
  }
`;

const SEARCH_CLEAR_BUTTON_STYLE = css`
  label: SEARCH_CLEAR_BUTTON_STYLE;
  position: relative;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 18px;
  height: 18px;
  flex-shrink: 0;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 8px;
    top: 1px;
    width: 2px;
    height: 16px;
    background: #1f1f1f;
    border-radius: 1px;
  }

  &::before {
    transform: rotate(45deg);
  }

  &::after {
    transform: rotate(-45deg);
  }
`;

const LOAD_MORE_PROPERTIES_STYLE = css`
  margin: 16px auto 0;
  border: none;
  background: transparent;
  color: #2d88e6;
  font-size: ${pxToRem(13)};
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  display: block;
  text-align: center;
`;

const PropertyDetails = ({ property }: { property: ApiOrgProperty }) => {
  const facts = property?.customerMetadata;
  return !facts ? null : (
    <div>
      <div css={rowStyle}>
        <div>{facts?.bedroomCount ?? STRING_CONSTANTS.missingDataString} beds</div>
        <div css={barStyle}>|</div>
        <div>{presentBathCount(facts?.fullBathCount, facts?.halfBathCount, facts?.bathroomCount)} baths</div>
      </div>
      <div css={rowStyle}>
        <div>{facts?.squareFeet ? commaize(facts.squareFeet) : STRING_CONSTANTS.missingDataString} sqft</div>
        <div css={barStyle}>|</div>
        <div>built {facts?.vintage ?? STRING_CONSTANTS.missingDataString}</div>
      </div>
    </div>
  );
};

const PropertyMarket = ({ orgMarketId }: { orgMarketId?: string | undefined | null }) => {
  const { marketsById } = useOrgMarkets();

  if (!orgMarketId) {
    return <span>{STRING_CONSTANTS.missingDataString}</span>;
  }

  const market = marketsById?.[orgMarketId];
  return <span>{market?.name ?? STRING_CONSTANTS.missingDataString}</span>;
};

const propertyMatchesSearch = (property: ApiOrgProperty, search: string) => (
  property.address.toLowerCase().includes(search)
  || (property.name ?? '').toLowerCase().includes(search)
  || (property.customerMetadata?.propertyId ?? '').toLowerCase().includes(search)
  || property.id.slice(0, 8).toLowerCase().includes(search)
);

const includeLocallyMatchingArchivedProperties = (
  searchResults: ApiOrgProperty[],
  archivedProperties: ApiOrgProperty[] | undefined,
  search: string,
) => [
  ...searchResults,
  ...(archivedProperties ?? []).filter(property =>
    !searchResults.some(result => result.id === property.id)
    && propertyMatchesSearch(property, search)
  ),
];

type AddPropertyFormProps = {
  markets: { id: string; name: string }[] | undefined;
  mbAddress: string;
  setMbAddress: (address: string) => void;
  setZipCode: (zipCode: string) => void;
  setPlaceLatitude: (latitude: number | null) => void;
  setPlaceLongitude: (longitude: number | null) => void;
  fetchMarketsByZipCode: (zipCode: string) => Promise<{ id: string }[]>;
  addProperty: (address: string, propertyId: string, orgMarketId?: string) => Promise<void>;
  showToast: (message: string, type: 'success' | 'error') => void;
  showErrorToast: (error: unknown, action: string) => void;
};

const AddPropertyForm = ({
  markets,
  mbAddress,
  setMbAddress,
  setZipCode,
  setPlaceLatitude,
  setPlaceLongitude,
  fetchMarketsByZipCode,
  addProperty,
  showToast,
  showErrorToast,
}: AddPropertyFormProps) => {
  const formikRef = useRef<{ setFieldValue: (field: string, value: string) => void } | null>(null);
  const propertyIdLabel = usePropertyIdLabel();

  return (
    <Formik<{ address: string; idNumber: string; orgMarketId: string }>
      initialValues={{
        address: '',
        idNumber: '',
        orgMarketId: '',
      }}
      validateOnMount
      validate={values => {
        const errors: Partial<Record<keyof typeof values, string>> = {};
        if (!values.address) errors.address = 'Address is required';
        return errors;
      }}
      onSubmit={async values => {
        try {
          await addProperty(values.address, values.idNumber, values.orgMarketId);
          showToast('Property created successfully', 'success');
        } catch (error) {
          showErrorToast(error, 'create a property');
        }
      }}
    >
      {({ handleSubmit, values, setFieldValue, isValid, isSubmitting }) => {
        formikRef.current = { setFieldValue };
        return (
          <StyledForm
            onSubmit={handleSubmit}
            onKeyDown={event => {
              if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
                event.preventDefault();
              }
            }}
          >
            <span>
              Enter or select a street address and enter an ID number to get started.
            </span>
            <GooglePlacesAutocompleteInput
              googlePlacesApiKey={(import.meta as any).env.VITE_GOOGLE_PLACES_API_KEY}
              label='Street Address'
              onPlaceSelected={async place => {
                const addressComponents = place.address_components || [];
                const getComponent = (type: string) => addressComponents.find(c => c.types.includes(type))?.short_name || '';

                const street = `${getComponent('street_number')} ${addressComponents.find(c => c.types.includes('route'))?.long_name || ''}`.trim();
                const city = addressComponents.find(c => c.types.includes('locality'))?.long_name || '';
                const state = getComponent('administrative_area_level_1');
                const postcode = getComponent('postal_code');

                const fullAddress = place.formatted_address || `${street}, ${city}, ${state} ${postcode}`;
                formikRef.current?.setFieldValue('address', fullAddress);
                setMbAddress(fullAddress);
                setZipCode(postcode);
                const latitudeLongitude = getLatitudeLongitudeFromPlaceResult(place);
                setPlaceLatitude(latitudeLongitude?.latitude ?? null);
                setPlaceLongitude(latitudeLongitude?.longitude ?? null);

                if (postcode) {
                  const marketsByZip = await fetchMarketsByZipCode(postcode);
                  if (marketsByZip.length > 0) {
                    formikRef.current?.setFieldValue('orgMarketId', marketsByZip[0].id);
                  }
                }
              }}
              options={{
                types: ['address'],
                componentRestrictions: { country: 'US' },
              }}
              value={mbAddress}
              onChange={val => {
                setMbAddress(val);
                setFieldValue('address', val);
                if (values.address && val !== values.address) {
                  setZipCode('');
                  setPlaceLatitude(null);
                  setPlaceLongitude(null);
                }
              }}
            />
            <FormikLabeledInput
              name='idNumber'
              label={propertyIdLabel}
              placeholder='Enter the ID number'
              optional
            />
            <FormikLabeledSelect
              name='orgMarketId'
              label='Market'
              items={markets ? markets.map(m => ({ value: m.id, label: m.name })) : []}
            />
            <Button
              color='green'
              disabled={!isValid || isSubmitting}
              type='submit'
            >
              Add Property
            </Button>
          </StyledForm>
        );
      }}
    </Formik>
  );
};

export const PropertiesPage = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const showArchived = searchParams.get('archived') === 'true';
  usePageTitle('Properties');
  const { properties: activeProperties, propertiesMutators } = useOrgProperties({ archived: false });
  const { properties: archivedProperties } = useOrgProperties({ archived: true });
  const properties = showArchived ? archivedProperties : activeProperties;
  const { orgId, userCan } = useAuth();
  const canCreateProperty = userCan('create', 'OrgProperty');
  const canUpdateProperty = userCan('update', 'OrgProperty');
  const { markets } = useOrgMarkets();
  const apiClient = useKaiProApiClient();
  const { showToast, showErrorToast } = useToast();
  const {
    searchText: propertySearchText,
    setSearchText: setPropertySearchText,
    debouncedSearchText: debouncedPropertySearchText,
    clearSearch: clearPropertySearch,
  } = usePersistedSearch('properties:search');
  const { order: sortOrder, orderBy: sortOrderBy, onSortChange } = usePersistedSort('properties:sort', 'desc', 'createdDate');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [visiblePropertyCount, setVisiblePropertyCount] = useState(PROPERTIES_LOAD_BATCH_SIZE);
  const propertySearchInputRef = useRef<HTMLInputElement>(null);
  const {
    results: searchedProperties,
    isLoading: isLoadingPropertySearch,
    searchAttempted: propertySearchAttempted,
  } = usePropertySearch(debouncedPropertySearchText, { limit: 50, includeArchived: true });
  const [mbAddress, setMbAddress] = useState('');
  const [zipCode, setZipCode] = useState<string>('');
  const [placeLatitude, setPlaceLatitude] = useState<number | null>(null);
  const [placeLongitude, setPlaceLongitude] = useState<number | null>(null);
  const navigate = useNavigate();
  const propertyToEdit = id
    ? (properties?.find(property => property.id === id) ?? searchedProperties.find(property => property.id === id) ?? null)
    : null;
  const flyoutOpen = id === 'new' || !!propertyToEdit;

  useEffect(() => {
    if (isSearchExpanded) propertySearchInputRef.current?.focus();
  }, [isSearchExpanded]);

  const normalizedSearchInput = propertySearchText.trim().toLowerCase();
  const isSearchSettled = debouncedPropertySearchText.toLowerCase() === normalizedSearchInput;

  useEffect(() => {
    setVisiblePropertyCount(PROPERTIES_LOAD_BATCH_SIZE);
  }, [normalizedSearchInput, showArchived]);

  const filteredProperties = useMemo(() => {
    if (!properties) return [];
    if (!normalizedSearchInput) return properties;

    const shouldApplyGlobalSearchResults = propertySearchAttempted && isSearchSettled && !isLoadingPropertySearch;
    if (!shouldApplyGlobalSearchResults) return properties;
    return includeLocallyMatchingArchivedProperties(searchedProperties, archivedProperties, normalizedSearchInput);
  }, [
    archivedProperties,
    isSearchSettled,
    isLoadingPropertySearch,
    normalizedSearchInput,
    properties,
    propertySearchAttempted,
    searchedProperties,
  ]);

  const fetchMarketsByZipCode = async (zipCode: string) => {
    try {
      const marketsByZip = await apiClient.getMarketsByZipCode(zipCode);
      return marketsByZip;
    } catch (error) {
      logError('Failed to fetch markets by zip code', error, { zipCode });
      return [];
    }
  };

  const addProperty = async (address: string, propertyId: string, orgMarketId?: string) => {
    if (orgId) {
      const newProperty = await propertiesMutators.create({
        address: address,
        customerMetadata: {
          propertyId: propertyId.trim() || null,
          zipCode: zipCode || null,
          ...(placeLatitude != null ? { latitude: placeLatitude } : {}),
          ...(placeLongitude != null ? { longitude: placeLongitude } : {}),
        },
        orgMarketId: orgMarketId || null,
      });
      trackEvent(ANALYTICS_EVENTS.propertyCreated, {
        propertyId: newProperty.id,
        hasMarket: !!orgMarketId,
        hasPropertyReference: !!propertyId.trim(),
      });
      navigate(`/property/${newProperty.id}`);
    }
  };

  const displayProperties = filteredProperties
    .map(property => ({
      id: property.id,
      image: <PropertyListImage property={property} />,
      createdDate: property.createdAt,
      idNumber: property.customerMetadata?.propertyId || 'N/A',
      address: property.address,
      market: <PropertyMarket orgMarketId={property.orgMarketId} />,
      propertyDetails: <PropertyDetails property={property} />,
      archiveStatus: property.archivedAt ? 'Archived' : 'Active',
      link: `/property/${property.id}`,
      menu: [
        {
          label: canUpdateProperty ? 'Edit Property Details' : 'View Property Details',
          action: () => navigate(`/properties/${property.id}`),
        },
        ...(canUpdateProperty
          ? [{
            label: property.archivedAt ? 'Unarchive' : 'Archive',
            action: async () => {
              const isArchiving = !property.archivedAt;
              await propertiesMutators.update(property.id, {
                archivedAt: property.archivedAt ? null : new Date().toISOString(),
              });
              // force invalidate both active and archived queries so properties move between lists correctly
              await propertiesMutators.refetchAllProperties();
              trackEvent(isArchiving ? ANALYTICS_EVENTS.propertyArchived : ANALYTICS_EVENTS.propertyUnarchived, {
                propertyId: property.id,
              });
            },
          }]
          : []),
      ],
    }));

  const hasAnyProperties = (properties?.length ?? 0) > 0;
  const haveProperties = displayProperties.length > 0;
  const canLoadMoreProperties = displayProperties.length > visiblePropertyCount;
  const nextLoadPropertyCount = Math.min(
    PROPERTIES_LOAD_BATCH_SIZE,
    Math.max(displayProperties.length - visiblePropertyCount, 0),
  );
  const hasActiveSearch = !!normalizedSearchInput;
  const canShowSearchEmptyState = normalizedSearchInput.length >= 3
    && propertySearchAttempted
    && isSearchSettled
    && !isLoadingPropertySearch
    && !haveProperties;
  const shouldShowExpandedSearch = isSearchExpanded || !!propertySearchText.trim();

  if (!properties) return <GenericLoadingPage />;

  return (
    <PageLayout
      title={'Properties' + (showArchived && !hasActiveSearch ? ' (archived)' : '')}
      contentLayoutStyling={'standard'}
    >
      <RecordListHeading
        recordType='Property'
        activeCount={activeProperties?.length ?? 0}
        archivedCount={archivedProperties?.length ?? 0}
        showArchived={showArchived}
        displayingAllArchiveStatuses={hasActiveSearch}
        displayCountOverride={hasActiveSearch ? displayProperties.length : undefined}
        actionNodes={[
          shouldShowExpandedSearch
            ? (
              <div key='propertySearch' css={SEARCH_BAR_STYLE}>
                <button
                  css={SEARCH_ICON_BUTTON_STYLE}
                  type='button'
                  onClick={() => {
                    if (!propertySearchText.trim()) setIsSearchExpanded(false);
                  }}
                  aria-label='Search'
                >
                  <img src={searchIcon} css={SEARCH_ICON_STYLE} alt='Search' />
                </button>
                <input
                  ref={propertySearchInputRef}
                  css={SEARCH_INPUT_STYLE}
                  type='search'
                  value={propertySearchText}
                  onChange={event => setPropertySearchText(event.target.value)}
                  placeholder='Search by address or property ID'
                  aria-label='Search properties'
                />
                {!!propertySearchText.trim() && (
                  <button
                    css={SEARCH_CLEAR_BUTTON_STYLE}
                    type='button'
                    onClick={() => {
                      clearPropertySearch();
                      setIsSearchExpanded(false);
                    }}
                    aria-label='Clear and close property search'
                  />
                )}
              </div>
            )
            : (
              <button
                key='propertySearchCollapsed'
                css={SEARCH_COLLAPSED_BUTTON_STYLE}
                type='button'
                onClick={() => setIsSearchExpanded(true)}
                aria-label='Open property search'
              >
                <img src={searchIcon} css={SEARCH_ICON_STYLE} alt='Search' />
              </button>
            ),
        ]}
        onCreate={canCreateProperty ? () => navigate('/properties/new') : undefined}
        onToggleArchived={() => {
          const newParams = new URLSearchParams(searchParams);
          if (showArchived) {
            newParams.delete('archived');
          } else {
            newParams.set('archived', 'true');
          }
          setSearchParams(newParams);
        }}
      />
      {!hasAnyProperties && !hasActiveSearch
        ? (
          <NoRecordsPlaceholder
            recordType='Properties'
            qualifier={showArchived ? 'Archived' : undefined}
            description={canCreateProperty ? 'Create your first property to get started.' : 'No properties have been created yet.'}
            createButtonText={canCreateProperty ? 'Add a Property' : undefined}
            createFirstRecord={canCreateProperty ? () => navigate('/properties/new') : undefined}
            iconType='properties'
          />
        )
        : canShowSearchEmptyState
        ? (
          <NoRecordsPlaceholder
            recordType='Properties'
            description='No properties found. Double-check the address or property ID and try again.'
            createButtonText='Clear Search'
            createFirstRecord={() => {
              clearPropertySearch();
            }}
            iconType='properties'
          />
        )
        : (
          <>
            <DataTable
              recordType='property'
              items={displayProperties}
              defaultOrder={sortOrder}
              defaultOrderBy={sortOrderBy}
              onSortChange={onSortChange}
              pageSize={visiblePropertyCount}
              recordRenderMax={displayProperties.length}
              columns={[
                { name: 'image', sortable: false, width: '160px' },
                { name: 'createdDate', sortable: true, sortMethod: 'date', width: '120px', format: (value: string) => new Date(value).toLocaleDateString() },
                { name: 'idNumber', sortable: true, width: '120px' },
                { name: 'address', sortable: true },
                { name: 'market', sortable: false, width: '150px' },
                { name: 'propertyDetails', sortable: false, width: '200px' },
                { name: 'archiveStatus', displayName: 'Archive Status', sortable: true, width: '140px' },
              ]}
            />
            {canLoadMoreProperties && (
              <button
                type='button'
                css={LOAD_MORE_PROPERTIES_STYLE}
                onClick={() => setVisiblePropertyCount(count => count + PROPERTIES_LOAD_BATCH_SIZE)}
              >
                {`LOAD ${nextLoadPropertyCount} MORE ${nextLoadPropertyCount === 1 ? 'PROPERTY' : 'PROPERTIES'}`}
              </button>
            )}
          </>
        )}
      <FlyOut
        open={flyoutOpen}
        flyOutClosed={() => navigate('/properties')}
        title={propertyToEdit ? (canUpdateProperty ? 'Edit Property Details' : 'Property Details') : 'Add a Property'}
        subheader={!propertyToEdit ? <FormFieldsRequirementNote variant='optional' /> : undefined}
      >
        {propertyToEdit
          ? (
            <PropertyEditPanel
              property={propertyToEdit}
              onClose={() => navigate('/properties')}
              readOnly={!canUpdateProperty}
            />
          )
          : (
            <AddPropertyForm
              markets={markets}
              mbAddress={mbAddress}
              setMbAddress={setMbAddress}
              setZipCode={setZipCode}
              setPlaceLatitude={setPlaceLatitude}
              setPlaceLongitude={setPlaceLongitude}
              fetchMarketsByZipCode={fetchMarketsByZipCode}
              addProperty={addProperty}
              showToast={showToast}
              showErrorToast={showErrorToast}
            />
          )}
      </FlyOut>
    </PageLayout>
  );
};
